import {
  useGetAppointmentByCodexAppointmentIdQuery,
  useGetProviderAppointmentByCodexAppointmentIdQuery,
  useGetProviderFullPatientInfoLazyQuery,
  useGetProviderInfoByProviderCodexIdLazyQuery,
} from 'graphql/generated/remote-schema-hasura';
import { useEffect, useRef } from 'react';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../firebase/interfaces';
import { useSearchParams } from 'react-router-dom';
import { CANCEL_APPOINTMENT_ID } from '../components/upcoming-appointments/helper/const';

export const useGetAppointmentDetails = (appointmentId: string) => {
  const [
    getProviderPatientInfo,
    { loading: patientLoading, data: patientData },
  ] = useGetProviderFullPatientInfoLazyQuery();
  const mediaMap = useRef<Map<string, string>>(new Map());
  const [url] = useSearchParams();

  const { user } = useAuth();
  const [
    getProviderInfo,
    { loading: providerDataLoading, data: providerData },
  ] = useGetProviderInfoByProviderCodexIdLazyQuery();

  const {
    data: appointmentPatientData,
    loading: appointmentPatientLoading,
    refetch: refetchAppointmentPatientData,
  } = useGetAppointmentByCodexAppointmentIdQuery({
    variables: { appointmentCodexId: appointmentId },
    skip: !appointmentId || user?.role !== Roles.PATIENT,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.getFHIRAppointmentByCodexId.appointment?.codexPatientId) {
        getProviderPatientInfo({
          variables: {
            patientCodexId:
              data.getFHIRAppointmentByCodexId.appointment.codexPatientId,
          },
        });
      }

      if (data.getFHIRAppointmentByCodexId.appointment?.codexProviderId) {
        getProviderInfo({
          variables: {
            providerCodexId:
              data.getFHIRAppointmentByCodexId.appointment?.codexProviderId,
          },
        });
      }

      if (data.getFHIRAppointmentByCodexId.appointment?.media?.length) {
        for (const { bodySite, fileId } of data.getFHIRAppointmentByCodexId
          .appointment.media) {
          if (bodySite && fileId) {
            mediaMap.current.set(bodySite, fileId);
          }
        }
      }
    },
  });

  const {
    data: appointmenProvidertData,
    loading: appointmentProviderLoading,
    refetch: refetchAppointmentProvider,
  } = useGetProviderAppointmentByCodexAppointmentIdQuery({
    variables: { appointmentCodexId: appointmentId },
    skip: !appointmentId || user?.role !== Roles.PROVIDER,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.getFHIRAppointmentByCodexId.appointment?.codexPatientId) {
        getProviderPatientInfo({
          variables: {
            patientCodexId:
              data.getFHIRAppointmentByCodexId.appointment.codexPatientId,
          },
        });
      }

      if (data.getFHIRAppointmentByCodexId.appointment?.codexProviderId) {
        getProviderInfo({
          variables: {
            providerCodexId:
              data.getFHIRAppointmentByCodexId.appointment?.codexProviderId,
          },
        });
      }

      if (data.getFHIRAppointmentByCodexId.appointment?.media?.length) {
        for (const { bodySite, fileId } of data.getFHIRAppointmentByCodexId
          .appointment.media) {
          if (bodySite && fileId) {
            mediaMap.current.set(bodySite, fileId);
          }
        }
      }
    },
  });

  const isAppointmentCancelModalOpen = !!url.get(CANCEL_APPOINTMENT_ID);
  const wasOpenCancelModal = useRef(false);

  useEffect(() => {
    if (isAppointmentCancelModalOpen) {
      wasOpenCancelModal.current = true;
      return;
    }

    if (wasOpenCancelModal.current) {
      refetchAppointmentProvider();
      refetchAppointmentPatientData();
      wasOpenCancelModal.current = false;
    }
  }, [
    isAppointmentCancelModalOpen,
    refetchAppointmentPatientData,
    refetchAppointmentProvider,
  ]);

  return {
    patientLoading,
    patientData,
    providerDataLoading,
    providerData,
    appointmentData: appointmenProvidertData || appointmentPatientData,
    appointmentLoading: appointmentPatientLoading || appointmentProviderLoading,
    mediaMap,
  };
};
